import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  X, Send, Save, FlaskConical, ShieldCheck, ShieldX, Activity,
  AlertCircle, AlertTriangle, Eye, UserPlus, Timer, Shuffle, CalendarClock,
  ArrowRight, HelpCircle, Info, Check, Cpu, XCircle, CheckCircle,
  ChevronDown, ChevronRight, Users, ThumbsUp,
} from 'lucide-react';
import { toast } from 'sonner';
import AIGenerateTemplatePanel from './AIGenerateTemplatePanel';
import { useAppMessage } from '../../../components/AppMessage';
import { fmtEST } from '../../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const VARS = ['first_name', 'last_name', 'company', 'title', 'city', 'industry'];

function inferAudience(url) {
  if (!url) return [];
  try {
    const u = new URL(url);
    const keywords = u.searchParams.get('keywords');
    const geoUrn = u.searchParams.getAll('geoUrn');
    const industry = u.searchParams.getAll('industry');
    const parts = [];
    if (keywords) parts.push(keywords);
    if (geoUrn?.length) parts.push('United States');   // geo 103644278 — pragmatic default
    if (industry?.length) parts.push('Software');
    return parts;
  } catch {
    return [];
  }
}

function renderPreview(template, recipient, audience = []) {
  if (!template) return '';
  // audience is inferred from search URL, e.g. ["CEOs", "United States", "Software"]
  // When recipient lacks a field we fall back to the campaign-level audience so the
  // rendered message reads naturally instead of showing placeholders like "their industry".
  const audienceRole = audience[0] || '';
  const audienceGeo = audience[1] || '';
  const audienceIndustry = audience[2] || '';
  const map = {
    first_name: recipient?.first_name || 'Priya',
    last_name: recipient?.last_name || '',
    company: recipient?.company || 'your company',
    title: recipient?.title || audienceRole || 'your role',
    industry: recipient?.industry || audienceIndustry || 'your industry',
    city: recipient?.city || audienceGeo || 'your area',
  };
  let out = template;
  Object.entries(map).forEach(([k, v]) => { out = out.replaceAll(`{${k}}`, v); });
  // Preserve line breaks — only collapse runs of spaces/tabs, not newlines.
  out = out.replace(/[ \t]+/g, ' ').replace(/[ \t]+([,.!?;])/g, '$1');
  return out;
}

export default function MessageCampaignDrawer({
  open,
  onClose,
  onLaunched,
  selectedRecipients = [],   // array of profile objects from LinkedinDataPage results
  platform = 'regular',
  searchUrl = '',
  extractionRunId = null,
}) {
  const [name, setName] = useState(() => localStorage.getItem('lnmsg_name') || '');
  const [messageTemplate, setMessageTemplate] = useState(() => localStorage.getItem('lnmsg_template') || '');
  const [connectNote, setConnectNote] = useState(() => localStorage.getItem('lnmsg_connect_note') || '');
  const [actions, setActions] = useState({
    visit_profile: true,
    like_recent_post: false,
    follow_profile: false,
    send_dm_first_degree: true,
    send_connect_request: true,
  });
  const [sections, setSections] = useState({
    actions: true,
    messageTemplate: false,
    connectNote: false,
    safety: false,
    schedule: false,
    preview: false,
    funnel: false,
    engine: false,
  });
  const toggleSection = (key) => setSections((s) => ({ ...s, [key]: !s[key] }));
  const [dailyPace, setDailyPace] = useState(100);
  const [gapMin, setGapMin] = useState(45);
  const [gapMax, setGapMax] = useState(180);
  const [coffeeEvery, setCoffeeEvery] = useState(12);
  const [live, setLive] = useState(false);
  const [aiPersonalize, setAiPersonalize] = useState(true);
  const [engine, setEngine] = useState('voyager');  // voyager = internal (default); apify only for SN/Recruiter
  const [startNow, setStartNow] = useState(false); // skip business-hours scheduling
  const [launching, setLaunching] = useState(false);
  const [watchdog, setWatchdog] = useState(null);
  const msg = useAppMessage();

  const audience = useMemo(() => inferAudience(searchUrl), [searchUrl]);

  const stats = useMemo(() => {
    const total = selectedRecipients.length;
    const first = selectedRecipients.filter((r) => r.degree === '1st' || !r.degree).length;
    const later = total - first;
    const dmCount = actions.send_dm_first_degree ? first : 0;
    const inviteCount = actions.send_connect_request ? later : 0;
    const followCount = actions.follow_profile ? total : 0;
    const likeCount = actions.visit_profile && actions.like_recent_post ? total : 0;
    // Visit-only mode: when no DM/invite actions enabled but visit is on, every recipient still gets visited.
    const visitOnly = actions.visit_profile && !actions.send_dm_first_degree && !actions.send_connect_request;
    const visitCount = visitOnly ? total : 0;
    const queued = dmCount + inviteCount + visitCount;
    const skipped = total - queued;
    const days = Math.max(1, Math.ceil(queued / Math.max(1, dailyPace)));
    return { total, first, later, dmCount, inviteCount, visitCount, followCount, likeCount, queued, skipped, days, visitOnly };
  }, [selectedRecipients, actions, dailyPace]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/linkedin-msg/watchdog/status?platform=${platform}`, { withCredentials: true })
      .then((r) => setWatchdog(r.data))
      .catch(() => {});
  }, [open, platform]);

  // Regular LinkedIn doesn't support Apify messenger — always use internal voyager engine.
  useEffect(() => { if (platform === 'regular') setEngine('voyager'); }, [platform]);

  // Persist user-editable fields to localStorage so the drawer feels sticky across sessions.
  useEffect(() => { localStorage.setItem('lnmsg_name', name || ''); }, [name]);
  useEffect(() => { localStorage.setItem('lnmsg_template', messageTemplate || ''); }, [messageTemplate]);
  useEffect(() => { localStorage.setItem('lnmsg_connect_note', connectNote || ''); }, [connectNote]);

  if (!open) return null;

  const insertVar = (setFn, current, v) => setFn((current || '') + `{${v}}`);

  const buildCampaignBody = (campaignName, isLive) => ({
    name: campaignName,
    platform,
    search_url: searchUrl,
    extraction_run_id: extractionRunId,
    recipient_profile_urls: selectedRecipients.map((r) => r.profile_url || r.linkedin_url).filter(Boolean),
    recipients: selectedRecipients
      .filter((r) => r.profile_url || r.linkedin_url)
      .map((r) => ({
        profile_url: r.profile_url || r.linkedin_url,
        first_name: r.first_name,
        last_name: r.last_name,
        title: r.title,
        company: r.company,
        city: r.city,
        industry: r.industry,
        email: r.email,
        degree: r.degree,
        linkedin_url: r.linkedin_url,
      })),
    engine,
    start_now: startNow,
    actions,
    message_template: messageTemplate,
    connect_note_template: connectNote,
    ai_personalize_per_recipient: aiPersonalize,
    audience,
    limits: {
      contacts_per_day: dailyPace,
      weekly_connect_cap: 100,
      daily_dm_cap: 50,
      daily_visit_cap: 300,
    },
    humanization: {
      gap_min_seconds: gapMin,
      gap_max_seconds: gapMax,
      coffee_break_every: coffeeEvery,
      coffee_break_min_minutes: 5,
      coffee_break_max_minutes: 15,
      warmup_half_speed_minutes: 30,
    },
    schedule: {
      timezone: 'America/New_York',
      active_hours_start: '09:00',
      active_hours_end: '17:00',
      send_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    },
    live: isLive,
  });

  const launch = async () => {
    // Auto-generate a name if the user didn't type one
    let finalName = name.trim();
    if (!finalName) {
      finalName = `Campaign ${fmtEST(new Date())}`;
      setName(finalName);
    }
    if (actions.send_dm_first_degree && !messageTemplate.trim())
      return msg.error('Message template required', 'You enabled "Send direct message" — provide a template body before launching.');
    if (actions.send_connect_request && !connectNote.trim())
      return msg.error('Connect note required', 'You enabled "Send connection request" — provide the note template before launching.');
    if (stats.queued === 0)
      return msg.error('No actions to queue', 'Enable at least one of Visit / DM / Connect, and make sure you have recipients selected.');

    setLaunching(true);
    try {
      const body = buildCampaignBody(finalName, live);
      const { data: created } = await axios.post(`${API}/api/linkedin-msg/campaigns`, body, { withCredentials: true });
      const { data: launched } = await axios.post(
        `${API}/api/linkedin-msg/campaigns/${created.id}/launch?confirm_live=${live}`,
        null,
        { withCredentials: true },
      );
      msg.success('Campaign launched', `${launched.enqueued ?? launched.batch_size ?? stats.queued} recipients queued · ${launched.live ? 'LIVE' : 'DRY-RUN'}`);
      onLaunched?.(created);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Launch failed');
    } finally {
      setLaunching(false);
    }
  };

  const saveDraft = async () => {
    if (!name.trim()) return msg.error('Campaign name required', 'Give this campaign a name so you can find it on the activity page.');
    setLaunching(true);
    try {
      const body = buildCampaignBody(name, false);
      await axios.post(`${API}/api/linkedin-msg/campaigns`, body, { withCredentials: true });
      msg.success('Draft saved');
      onClose?.();
    } catch (e) {
      msg.error('Save failed', e.response?.data?.detail || e.message, e.response?.data);
    } finally {
      setLaunching(false);
    }
  };

  const msgChars = messageTemplate.length;
  const noteChars = connectNote.length;
  const msgOverLimit = msgChars > 1200;
  const noteOverLimit = noteChars > 300;

  const preview3 = selectedRecipients.slice(0, 3);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" onClick={onClose} />

      <aside className="absolute top-0 right-0 h-full w-[640px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="bg-[#0A66C2] rounded-md p-1.5"><Send className="w-4 h-4 text-white" /></div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-gray-900">New Message Campaign</h2>
            <p className="text-xs text-gray-500">{stats.total} recipients selected</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Watchdog status strip */}
        <div className="px-5 py-2.5 bg-gradient-to-r from-emerald-50 via-emerald-50 to-white border-b border-emerald-100 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-emerald-900">AI Watchdog · Monitoring</span>
              <span className="text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">
                Risk {watchdog?.risk_score ?? 0}/100
              </span>
            </div>
            <p className="text-[11px] text-emerald-700 leading-tight">
              Will hard-stop before any ban-risk action
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* name */}
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Campaign name</label>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm"
              placeholder="e.g. US CEOs — software industry outreach"
            />
          </div>

          {/* per-recipient actions */}
          <div>
            <button type="button" onClick={() => toggleSection('actions')}
              className="w-full flex items-center justify-between py-1 group">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide cursor-pointer">Per-recipient actions</label>
              {sections.actions ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {sections.actions && (
              <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100">
                <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={actions.visit_profile}
                    onChange={(e) => setActions({ ...actions, visit_profile: e.target.checked })}
                    className="mt-0.5 rounded border-gray-300 text-[#0A66C2]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Eye className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">Visit profile first</span>
                      <span className="text-[10px] bg-gray-50 text-gray-600 border border-gray-200 rounded-full px-2 py-0.5">step 1</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Opens each profile 6–12s — triggers "viewed your profile" notification.</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">~300/day safe</span>
                </label>

                {actions.visit_profile && (
                  <label className="flex items-start gap-3 pl-10 pr-4 py-2.5 cursor-pointer hover:bg-rose-50/40 bg-gray-50/60 border-t border-dashed border-gray-100">
                    <input type="checkbox" checked={actions.like_recent_post}
                      onChange={(e) => setActions({ ...actions, like_recent_post: e.target.checked })}
                      className="mt-0.5 rounded border-gray-300 text-rose-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <ThumbsUp className="w-3.5 h-3.5 text-rose-500" />
                        <span className="text-sm font-medium text-gray-800">Like recent post</span>
                        <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-100 rounded-full px-2 py-0.5">if posted</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Likes most recent post during visit. Skipped if no recent activity or already liked.</p>
                    </div>
                    <span className="text-xs text-rose-600 whitespace-nowrap">no dup</span>
                  </label>
                )}

                <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={actions.follow_profile}
                    onChange={(e) => setActions({ ...actions, follow_profile: e.target.checked })}
                    className="mt-0.5 rounded border-gray-300 text-[#0A66C2]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-violet-600" />
                      <span className="text-sm font-medium text-gray-900">Follow profile</span>
                      <span className="text-[10px] bg-violet-50 text-violet-700 border border-violet-100 rounded-full px-2 py-0.5">any degree</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Follows the profile if they have Follow available (creators, public figures). Skipped silently if not.</p>
                  </div>
                  <span className="text-xs text-violet-700 whitespace-nowrap">~150/day safe</span>
                </label>

                <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={actions.send_dm_first_degree}
                    onChange={(e) => setActions({ ...actions, send_dm_first_degree: e.target.checked })}
                    className="mt-0.5 rounded border-gray-300 text-[#0A66C2]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Send className="w-3.5 h-3.5 text-[#0A66C2]" />
                      <span className="text-sm font-medium text-gray-900">Send direct message</span>
                      <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2 py-0.5">1st connections</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Uses the Message Template below. Only 1st-degree.</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">~50/day safe</span>
                </label>

                <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 bg-amber-50/30">
                  <input type="checkbox" checked={actions.send_connect_request}
                    onChange={(e) => setActions({ ...actions, send_connect_request: e.target.checked })}
                    className="mt-0.5 rounded border-gray-300 text-[#0A66C2]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-sm font-medium text-gray-900">Send connection request</span>
                      <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">2nd / 3rd</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Send a connect request with note (300-char cap). Off → skip non-1st profiles.</p>
                  </div>
                  <span className="text-xs text-amber-700 whitespace-nowrap">~100/week safe</span>
                </label>
              </div>
            )}
          </div>

          {/* Message template */}
          {actions.send_dm_first_degree && (
            <div>
              <button type="button" onClick={() => toggleSection('messageTemplate')}
                className="w-full flex items-center justify-between py-1">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide cursor-pointer">
                  Message template <span className="text-blue-700 normal-case font-normal">· 1st connections only</span>
                </label>
                {sections.messageTemplate ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {sections.messageTemplate && (<>
              <div className="flex items-center justify-between mb-2 mt-2">
                <span />
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={aiPersonalize}
                    onChange={(e) => setAiPersonalize(e.target.checked)}
                    className="rounded border-gray-300 text-[#0A66C2]" />
                  AI-personalize per-recipient
                </label>
              </div>

              <AIGenerateTemplatePanel
                kind="message"
                audience={audience}
                onGenerated={(text) => setMessageTemplate(text)}
              />

              <div className="flex flex-wrap gap-1.5 mb-2">
                {VARS.map((v) => (
                  <button key={v} type="button"
                    onClick={() => insertVar(setMessageTemplate, messageTemplate, v)}
                    className="text-xs font-mono px-2 py-1 rounded-md border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 text-gray-700">
                    {`{${v}}`}
                  </button>
                ))}
              </div>

              <textarea
                rows={6}
                value={messageTemplate}
                onChange={(e) => setMessageTemplate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-md text-sm leading-relaxed"
                placeholder={'Hi {first_name},\n\nSaw you work at {company} as {title} — we\'ve been helping teams in {industry} reduce…\n\nWould you be open to a quick 15-min chat next week?'}
              />

              <div className="flex items-center justify-between mt-1.5 text-xs">
                <span className={msgOverLimit ? 'text-amber-700' : 'text-gray-500'}>
                  {msgChars} / 1200 chars{msgOverLimit ? ' — over limit' : ''}
                </span>
                <span className="text-gray-400">LinkedIn DM cap ~8000</span>
              </div>
              </>)}
            </div>
          )}

          {/* Connect note template */}
          {actions.send_connect_request && (
            <div>
              <button type="button" onClick={() => toggleSection('connectNote')}
                className="w-full flex items-center justify-between py-1">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide cursor-pointer">
                  Connect request note <span className="text-amber-800 normal-case font-normal">· 2nd / 3rd</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Hard 300-char cap
                  </span>
                  {sections.connectNote ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </div>
              </button>
              {sections.connectNote && (<>

              <AIGenerateTemplatePanel
                kind="connect_note"
                audience={audience}
                onGenerated={(text) => setConnectNote(text)}
              />

              <div className="flex flex-wrap gap-1.5 mb-2">
                {['first_name', 'company', 'title', 'industry'].map((v) => (
                  <button key={v} type="button"
                    onClick={() => insertVar(setConnectNote, connectNote, v)}
                    className="text-xs font-mono px-2 py-1 rounded-md border border-gray-200 bg-gray-50 hover:bg-amber-50 hover:border-amber-200 text-gray-700">
                    {`{${v}}`}
                  </button>
                ))}
              </div>

              <textarea
                rows={3}
                maxLength={300}
                value={connectNote}
                onChange={(e) => setConnectNote(e.target.value)}
                className="w-full px-3 py-2.5 border border-amber-200 rounded-md text-sm leading-relaxed bg-amber-50/20"
                placeholder="Hi {first_name} — came across your profile while looking at {industry} leaders. Would love to connect and swap notes on AI-assisted hiring. Not selling — just curious how you think about it."
              />
              <div className="flex items-center justify-between mt-1.5 text-xs">
                <span className={noteOverLimit ? 'text-rose-700' : 'text-gray-500'}>
                  {noteChars} / 300 chars
                </span>
                <span className="text-gray-400">Free accounts: ~5 notes/month max</span>
              </div>
              </>)}
            </div>
          )}

          {/* Safety & humanization */}
          <div className="border border-amber-200 bg-amber-50 rounded-lg">
            <button type="button" onClick={() => toggleSection('safety')}
              className="w-full flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-900">Rate limits & account safety</h3>
              </div>
              {sections.safety ? <ChevronDown className="w-4 h-4 text-amber-600" /> : <ChevronRight className="w-4 h-4 text-amber-600" />}
            </button>
            {sections.safety && (<div className="px-4 pb-4">
            <div className="rounded-md bg-white border border-amber-100 px-3 py-2.5 mb-2">
              <div className="text-[11px] uppercase tracking-wide text-amber-700 font-medium mb-2 flex items-center gap-1">
                <Timer className="w-3 h-3" /> Gap between profiles (humanization)
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-amber-700 block mb-0.5">Min (s)</label>
                  <input type="number" value={gapMin} onChange={(e) => setGapMin(parseInt(e.target.value) || 20)}
                    className="h-8 w-full px-2 border border-amber-200 bg-amber-50/40 rounded text-xs font-semibold text-amber-900" />
                </div>
                <span className="text-amber-700 text-xs mt-4">–</span>
                <div className="flex-1">
                  <label className="text-[10px] text-amber-700 block mb-0.5">Max (s)</label>
                  <input type="number" value={gapMax} onChange={(e) => setGapMax(parseInt(e.target.value) || 60)}
                    className="h-8 w-full px-2 border border-amber-200 bg-amber-50/40 rounded text-xs font-semibold text-amber-900" />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-amber-700 block mb-0.5">Coffee every</label>
                  <div className="flex items-center gap-1">
                    <input type="number" value={coffeeEvery} onChange={(e) => setCoffeeEvery(parseInt(e.target.value) || 0)}
                      className="h-8 w-14 px-2 border border-amber-200 bg-amber-50/40 rounded text-xs font-semibold text-amber-900" />
                    <span className="text-[11px] text-amber-700">→ 5-15m</span>
                  </div>
                </div>
              </div>
            </div>
            </div>)}
          </div>

          {/* Schedule */}
          <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <button type="button" onClick={() => toggleSection('schedule')}
              className="w-full flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-[#0A66C2]" />
                <h3 className="text-sm font-semibold text-gray-900">Schedule & daily pace</h3>
              </div>
              <div className="flex items-center gap-2">
                <label onClick={(e) => e.stopPropagation()} className={`inline-flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                  startNow ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
                }`}>
                  <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} className="hidden" />
                  <Send className="w-3 h-3" />
                  {startNow ? 'Run NOW' : 'Run NOW'}
                </label>
                {sections.schedule ? <ChevronDown className="w-4 h-4 text-blue-400" /> : <ChevronRight className="w-4 h-4 text-blue-400" />}
              </div>
            </button>
            {sections.schedule && (<div className="px-4 pb-4">
            {startNow && (
              <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
                First <strong>{dailyPace}</strong> recipients start immediately (skipping business-hours wait). Remaining
                spread across next {Math.max(0, stats.days - 1)} business day(s).
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Contacts per day</label>
                <div className="flex items-center gap-2">
                  <input type="number" value={dailyPace}
                    onChange={(e) => setDailyPace(Math.max(1, parseInt(e.target.value) || 100))}
                    min={1} max={300}
                    className="h-10 w-24 px-3 border border-gray-200 rounded-md text-sm font-semibold text-gray-900" />
                  <div className="flex gap-1">
                    {[25, 50, 100, 150].map((n) => (
                      <button key={n} onClick={() => setDailyPace(n)}
                        className={`text-[11px] px-2 py-1 rounded border ${dailyPace === n ? 'border-[#0A66C2] bg-blue-50 text-[#0A66C2] font-semibold' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">LinkedIn safe-zone: up to ~150 total actions/day</p>
              </div>
              <div className="text-gray-300 text-xl self-center mb-1">→</div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Auto-queued over</label>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">{stats.days}</span>
                  <span className="text-sm text-gray-600">business days</span>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">Mon-Fri · 9am–5pm EST</p>
              </div>
            </div>
            </div>)}
          </div>

          {/* Preview */}
          {preview3.length > 0 && (messageTemplate || connectNote) && (
            <div className="border border-gray-200 rounded-lg">
              <button type="button" onClick={() => toggleSection('preview')}
                className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Preview ({preview3.length})</h3>
                </div>
                {sections.preview ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {sections.preview && <div className="divide-y divide-gray-100">
                {preview3.map((r, i) => {
                  const useNote = (r.degree && r.degree !== '1st') && actions.send_connect_request;
                  const body = renderPreview(useNote ? connectNote : messageTemplate, r, audience);
                  return (
                    <div key={i} className="p-4 flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-200 to-indigo-400 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                        {(r.first_name?.[0] || '?') + (r.last_name?.[0] || '')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{r.first_name} {r.last_name}</p>
                          <span className="text-[11px] text-gray-400">{useNote ? 'Connect request' : 'DM (1st)'}</span>
                        </div>
                        <p className="text-xs text-gray-600 truncate">{r.title} · {r.company}</p>
                        <div className="mt-2 rounded-md bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-800 leading-relaxed whitespace-pre-wrap">
                          {body || <em className="text-gray-400">no template</em>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>}
            </div>
          )}

          {/* Recipients funnel */}
          <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white">
            <button type="button" onClick={() => toggleSection('funnel')}
              className="w-full flex items-center justify-between px-4 py-3">
              <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Recipients funnel</div>
              {sections.funnel ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {sections.funnel && (<div className="px-4 pb-4">
            <div className="flex items-center gap-1 text-sm flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200">
                <span className="font-semibold text-gray-900">{stats.total}</span>
                <span className="text-gray-500">selected</span>
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 border border-blue-200 text-[#0A66C2]">
                <span className="font-semibold">{stats.queued}</span>
                <span>queued</span>
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-6 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="text-gray-700 font-medium">{stats.visitCount || (actions.visit_profile ? stats.total : 0)}</span>
                <span className="text-gray-500">Visits</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                <span className="text-gray-700 font-medium">{stats.likeCount}</span>
                <span className="text-gray-500">Likes</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                <span className="text-gray-700 font-medium">{stats.followCount}</span>
                <span className="text-gray-500">Follow</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-gray-700 font-medium">{stats.dmCount}</span>
                <span className="text-gray-500">DM (1st)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-700 font-medium">{stats.inviteCount}</span>
                <span className="text-gray-500">Connect</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-700 font-medium">{stats.skipped}</span>
                <span className="text-gray-500">skipped</span>
              </div>
            </div>
            </div>)}
          </div>

          {/* Engine selector */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <button type="button" onClick={() => toggleSection('engine')}
              className="w-full flex items-center justify-between px-3 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">Execution engine</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {engine === 'apify'
                    ? 'Apify actor — residential proxy, humanized delays, best for safety'
                    : 'Internal Voyager + Playwright — runs on our server, more control'}
                </p>
              </div>
              {sections.engine ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {sections.engine && (<div className="px-3 pb-3">
              <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                <button type="button"
                  onClick={() => setEngine('apify')}
                  className={`text-xs px-3 py-1 rounded ${engine === 'apify' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
                  Apify
                </button>
                <button type="button"
                  onClick={() => setEngine('voyager')}
                  className={`text-xs px-3 py-1 rounded ${engine === 'voyager' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
                  Internal
                </button>
              </div>
            </div>)}
          </div>

          {/* Live toggle */}
          <div className={`rounded-lg border-2 ${live ? 'border-rose-300 bg-rose-50' : 'border-gray-200 bg-gray-50'} p-3`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)}
                className="rounded border-gray-300 text-rose-600" />
              <span className={`text-sm font-semibold ${live ? 'text-rose-800' : 'text-gray-700'}`}>
                {live ? '⚠ LIVE mode — will actually message LinkedIn users' : 'DRY-RUN mode (default, safe)'}
              </span>
            </label>
            <p className="text-[11px] text-gray-600 mt-1">
              Dry-run logs every intended action without hitting LinkedIn. Turn LIVE on only after you've reviewed preview and schedule.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3 bg-white">
          <div className="flex items-center gap-2">
            <button
              onClick={launch} disabled={launching}
              className="flex-1 h-11 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 text-white disabled:opacity-50"
              style={{ background: '#0A66C2' }}
            >
              <Send className="w-4 h-4" />
              {launching ? 'Launching…' : `Queue ${stats.queued} · Watchdog approved`}
              <ShieldCheck className="w-4 h-4" />
            </button>
            <button onClick={saveDraft} disabled={launching}
              className="h-11 px-3 rounded-md text-sm font-medium border border-gray-200 bg-white hover:bg-gray-50 inline-flex items-center gap-1.5 text-gray-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> Draft
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            Voyager + Playwright hybrid · {dailyPace}/day · {gapMin}–{gapMax}s gaps · coffee-breaks · watchdog auto-pauses on any LinkedIn signal
          </p>
        </div>
      </aside>
    </div>
  );
}
